import { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { decodeReceipt, ReceiptData } from '@/lib/receipt';
import { formatBCH, formatFiat } from '@/lib/bch';
import { PUSD } from '@/lib/stablecoin';

function formatDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ReceiptCard({ data }: { data: ReceiptData }) {
  const isOfflineTx = data.txid.startsWith('offline-');
  const explorerUrl = isOfflineTx ? null : `https://bchexplorer.cash/tx/${data.txid}`;
  const sym = (() => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? data.currency;
    } catch {
      return data.currency;
    }
  })();

  return (
    <div className="w-full max-w-sm mx-auto">
      {/* Receipt card */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-zinc-100">
        {/* Header band */}
        <div className="px-6 py-6 text-center" style={{ backgroundColor: data.brandColor ?? '#0AC18E' }}>
          {data.logoDataUrl ? (
            <div className="flex justify-center mb-3">
              <img
                src={data.logoDataUrl}
                alt={data.merchantName}
                className="h-14 max-w-[180px] object-contain"
              />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-black text-sm">BCH</span>
            </div>
          )}
          <h1 className="text-white font-black text-xl">{data.merchantName}</h1>
          {data.merchantTagline && (
            <p className="text-white/70 text-sm mt-0.5">{data.merchantTagline}</p>
          )}
          <p className="text-white/60 text-xs mt-1">{formatDateTime(data.timestamp)}</p>
        </div>

        {/* Paid badge */}
        <div className="flex justify-center -mt-4 mb-2">
          <span className="bg-white text-xs font-black px-4 py-1 rounded-full shadow-sm tracking-widest uppercase border-2" style={{ color: data.brandColor ?? '#0AC18E', borderColor: data.brandColor ?? '#0AC18E' }}>
            ✓ Paid
          </span>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Line items */}
          {data.items && data.items.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Items</p>
              <div className="divide-y divide-zinc-100">
                {data.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm text-zinc-500 shrink-0">{item.qty}×</span>
                      <span className="text-sm font-medium text-zinc-800 truncate">{item.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-zinc-700 shrink-0 ml-3">
                      {sym}{(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="border-t-2 border-dashed border-zinc-100" />

          {/* Note */}
          {data.note && (
            <div className="bg-zinc-50 rounded-xl px-3 py-2.5 border border-zinc-100">
              <p className="text-xs text-zinc-500 font-medium">📝 {data.note}</p>
            </div>
          )}

          {/* Full breakdown */}
          {(data.discountAmount > 0 || data.taxRate > 0 || data.tipAmount > 0) && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm text-zinc-500">
                <span>Subtotal</span>
                <span>{formatFiat(data.subtotal, data.currency)}</span>
              </div>
              {data.discountAmount > 0 && (
                <div className="flex justify-between text-sm font-semibold text-red-500">
                  <span>Discount{data.discountLabel ? ` (${data.discountLabel})` : ''}</span>
                  <span>−{formatFiat(data.discountAmount, data.currency)}</span>
                </div>
              )}
              {data.taxRate > 0 && (
                <div className="flex justify-between text-sm text-zinc-500">
                  <span>{data.taxName} ({data.taxRate}%)</span>
                  <span>{formatFiat(data.taxAmount, data.currency)}</span>
                </div>
              )}
              {data.tipAmount > 0 && (
                <div className="flex justify-between text-sm font-semibold" style={{ color: data.brandColor ?? '#0AC18E' }}>
                  <span>Tip</span>
                  <span>{formatFiat(data.tipAmount, data.currency)}</span>
                </div>
              )}
            </div>
          )}

          {/* Total */}
          <div className="flex items-end justify-between">
            <span className="text-sm font-semibold text-zinc-500">Total</span>
            <div className="text-right">
              <p className="text-3xl font-black text-zinc-900">
                {formatFiat(data.fiatAmount, data.currency)}
              </p>
              <p className="flex items-center justify-end gap-1.5 text-sm font-mono font-semibold" style={{ color: data.brandColor ?? '#0AC18E' }}>
                {data.asset === 'PUSD' && data.tokenAmount != null ? (
                  <>
                    {PUSD.iconUrl && <img src={PUSD.iconUrl} alt="" className="h-4 w-4 rounded-full" />}
                    {`${data.tokenAmount.toFixed(2)} ${data.tokenSymbol ?? 'PUSD'}`}
                  </>
                ) : (
                  `${formatBCH(data.bchAmount)} BCH`
                )}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-2 border-dashed border-zinc-100" />

          {/* Transaction info */}
          <div className="space-y-2">
            <div>
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Transaction ID</p>
              {explorerUrl ? (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-mono text-[#0AC18E] break-all hover:underline leading-relaxed"
                >
                  {data.txid}
                </a>
              ) : (
                <p className="text-xs font-mono text-amber-500 leading-relaxed">
                  Confirmed offline — no blockchain transaction ID
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Paid to</p>
              <p className="text-xs font-mono text-zinc-500 break-all leading-relaxed">
                {data.address}
              </p>
            </div>
          </div>

          {/* Verify on explorer */}
          {explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-zinc-50 border border-zinc-200 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 transition-colors"
              style={{ color: data.brandColor ?? '#0AC18E' }}
            >
              <span>🔍</span> Verify on Block Explorer
            </a>
          )}
        </div>

        {/* Footer */}
        <div className="bg-zinc-50 border-t border-zinc-100 px-6 py-4 text-center">
          <p className="text-xs text-zinc-400">
            Paid with Bitcoin Cash •{' '}
            <a
              href="https://bchpay.cash"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0AC18E] hover:underline"
            >
              Powered by BCHPAY.Cash
            </a>
          </p>
        </div>
      </div>

      {/* Action buttons — hidden when printing */}
      <div className="no-print flex flex-col sm:flex-row gap-3 mt-4 w-full max-w-sm mx-auto">
        <button
          onClick={() => window.print()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#0AC18E] text-white text-sm font-bold shadow-lg shadow-[#0AC18E]/20 hover:bg-[#09AF81] active:scale-95 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print Receipt
        </button>
        <button
          onClick={() => {
            try { navigator.clipboard.writeText(window.location.href); } catch { /* ignore */ }
          }}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-zinc-200 text-zinc-600 text-sm font-semibold hover:bg-zinc-50 active:scale-95 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Copy Link
        </button>
      </div>
      <p className="no-print text-center text-xs text-zinc-400 mt-3 px-4">
        Print or screenshot to save your receipt
      </p>
    </div>
  );
}

function InvalidReceipt() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center gap-4">
      <div className="text-5xl">❌</div>
      <h1 className="text-xl font-bold text-zinc-800 dark:text-zinc-100">Invalid Receipt</h1>
      <p className="text-sm text-zinc-400 max-w-xs">
        This receipt link is invalid or has been corrupted. Please ask the merchant to share it again.
      </p>
      <Link
        to="/"
        className="mt-2 px-6 py-3 rounded-2xl bg-[#0AC18E] text-white font-bold text-sm shadow-lg shadow-[#0AC18E]/30"
      >
        Go to BCHPAY.Cash
      </Link>
    </div>
  );
}

export function ReceiptPage() {
  const { hash } = useLocation();

  const data = useMemo(() => {
    if (!hash || hash === '#') return null;
    return decodeReceipt(hash);
  }, [hash]);

  useSeoMeta({
    title: data ? `Receipt — ${data.merchantName}` : 'Receipt',
    description: data
      ? `Payment receipt from ${data.merchantName} for ${formatFiat(data.fiatAmount, data.currency)}`
      : 'BCH payment receipt',
  });

  if (!data) return <InvalidReceipt />;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex flex-col items-center justify-start py-8 px-4">
      <ReceiptCard data={data} />
    </div>
  );
}
